/**
 * Authentication slice - manages user authentication state
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { User, RequestStatus, ErrorState } from '../../types';

export interface AuthState {
  user: User | null;
  token: string | null;
  status: RequestStatus;
  error: ErrorState | null;
  isAuthenticated: boolean;
}

// In dev mode the token must be present before the first render so Apollo's
// initial requests include the Authorization header (authLink reads from store
// synchronously — a useEffect-based approach sets it too late).
//
// DEV_MODE is read inline inside the lazy initial-state builder, not hoisted into
// a module constant or helper function (#2165):
// - RTK calls the builder each time the reducer sees `undefined` state, so tests
//   can toggle the env with `vi.stubEnv` without re-importing this module.
// - In a build, Vite replaces the expression with a string literal, and the
//   minifier folds `"false" === 'true'` and drops the dev branch. A helper
//   function is not inlined by the minifier, so it would ship the dev user and
//   placeholder token in production bundles as unreachable code.

// Not a credential: DEV_MODE-only placeholder. The backend's OKTA_DEV_MODE
// path never validates this value, so it carries no authentication weight.
export const DEV_MODE_PLACEHOLDER_TOKEN = 'dev-mode-placeholder-token';

function buildInitialState(): AuthState {
  return import.meta.env.VITE_OKTA_DEV_MODE === 'true'
    ? {
        user: {
          id: 'dev-user',
          email: 'dev@example.com',
          name: 'Dev User',
          roles: ['Administrator'],
          teams: [],
        },
        token: DEV_MODE_PLACEHOLDER_TOKEN,
        status: 'succeeded',
        error: null,
        isAuthenticated: true,
      }
    : {
        user: null,
        token: null,
        status: 'idle',
        error: null,
        isAuthenticated: false,
      };
}

const authSlice = createSlice({
  name: 'auth',
  initialState: buildInitialState,
  reducers: {
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      state.status = 'succeeded';
      state.error = null;
    },
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
    },
    setAuthLoading: (state) => {
      state.status = 'loading';
      state.error = null;
    },
    setAuthError: (state, action: PayloadAction<ErrorState>) => {
      state.status = 'failed';
      state.error = action.payload;
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.status = 'idle';
      state.error = null;
    },
    clearAuthError: (state) => {
      state.error = null;
    },
  },
});

export const { setUser, setToken, setAuthLoading, setAuthError, logout, clearAuthError } =
  authSlice.actions;

export default authSlice.reducer;

// Selectors
export const selectUser = (state: { auth: AuthState }): User | null => state.auth.user;
export const selectToken = (state: { auth: AuthState }): string | null => state.auth.token;
export const selectIsAuthenticated = (state: { auth: AuthState }): boolean =>
  state.auth.isAuthenticated;
export const selectAuthStatus = (state: { auth: AuthState }): RequestStatus => state.auth.status;
export const selectAuthError = (state: { auth: AuthState }): ErrorState | null => state.auth.error;
